import { Line, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ComposedChart, Tooltip, ReferenceLine, ReferenceArea } from 'recharts';
import { Paper, IconButton } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import { useRef, useEffect, useState, useMemo } from 'react';
import type { ElevationPoint } from '../../types.js';
import { aggregateElevationData } from '../../utils/elevationAggregation.js';
import './ElevationProfile.css';

interface ElevationProfileProps {
  data: ElevationPoint[];
  onHover: (index: number | null) => void;
  variant?: 'default' | 'detail';
  routeInfo?: {
    distance: number;
    duration: number;
    ascend: number;
    coordinates?: Array<{ lat: number; lng: number; distance?: number }>; // Add coordinates for accurate hover mapping
  };
  onHoverInfoChange?: (hoverInfo: { distance: number; time: string; gradient: number } | null) => void;
  stickyHover?: boolean; // New prop to control sticky hover behavior
  onClearStickyHover?: () => void; // Callback to clear sticky hover
  activePoint?: number | null; // Current active point index from parent
  highlightedClimbIndex?: number | null; // Index of climb to highlight
  climbs?: any[]; // Climb data for highlighting
  fullElevationData?: ElevationPoint[]; // Full resolution elevation data for zoom re-aggregation
}

export const ElevationProfile = ({ 
  data, 
  onHover, 
  variant = 'default', 
  routeInfo, 
  onHoverInfoChange, 
  stickyHover = false, 
  onClearStickyHover, 
  activePoint,
  highlightedClimbIndex,
  climbs,
  fullElevationData
}: ElevationProfileProps) => {
  const timeoutRef = useRef<number | null>(null);
  
  // X-axis zoom state (distance-based zoom)
  const [zoomState, setZoomState] = useState({
    startDistance: 0,
    endDistance: 0,
    isZoomed: false
  });
  
  // Touch handling refs for zoom and pan
  const chartContainerRef = useRef<HTMLDivElement>(null);
  
  // Store callback ref to avoid dependency issues in useEffect
  const onHoverInfoChangeRef = useRef(onHoverInfoChange);
  
  // Store climbs ref to avoid dependency issues (climbs array might be recreated on every render)
  const climbsRef = useRef(climbs);
  
  // Store routeInfo ref to avoid dependency issues (might be recreated on every render)
  const routeInfoRef = useRef(routeInfo);
  
  // Flag to prevent sticky point management during zoom operations
  const isZoomingRef = useRef<boolean>(false);
  
  // State to control activeDot visibility - hide during and after zoom until user hovers again
  const [hideActiveDot, setHideActiveDot] = useState<boolean>(false);
  
  // State to store the hover distance for ReferenceLine (not an index, but actual distance value)
  const [hoverDistance, setHoverDistance] = useState<number | null>(null);
  
  // Track multi-touch state to prevent hover events during pinch/zoom
  const isMultiTouchRef = useRef<boolean>(false);
  
  // Throttle mouse move events for better performance
  const lastMouseMoveRef = useRef<number>(0);
  const MOUSE_MOVE_THROTTLE_MS = 0; // No throttle for instant response
  
  // Overview bar interaction refs
  const overviewDragRef = useRef<{ 
    isDragging: boolean, 
    startX: number,
    lastUpdateTime: number,
    animationFrameId?: number
  } | null>(null);
  const overviewElementRef = useRef<HTMLDivElement>(null);
  
  // Edge handle drag refs
  const leftEdgeDragRef = useRef<{ 
    isDragging: boolean, 
    startX: number,
    initialStartDistance: number,
    lastUpdateTime: number,
    animationFrameId?: number
  } | null>(null);
  const rightEdgeDragRef = useRef<{ 
    isDragging: boolean, 
    startX: number,
    initialEndDistance: number,
    lastUpdateTime: number,
    animationFrameId?: number
  } | null>(null);

  // Erzeuge gleichmäßig verteilte Datenpunkte für die Chart-Darstellung und Hoverpunkte
  const generateUniformData = (data: ElevationPoint[], numPoints = 150, startDist?: number, endDist?: number) => {
    if (data.length === 0) return [];
    
    // Use provided start/end distances for absolute positioning, or derive from data
    const rangeStart = startDist ?? data[0].distance;
    const rangeEnd = endDist ?? data[data.length - 1].distance;
    const rangeLength = rangeEnd - rangeStart;
    
    if (rangeLength === 0) return [data[0]];
    
    const uniformData: ElevationPoint[] = [];
    for (let i = 0; i < numPoints; i++) {
      const targetDist = rangeStart + (i / (numPoints - 1)) * rangeLength;
      
      // Find the interval in the original data array
      let j = 0;
      while (j < data.length - 1 && data[j + 1].distance < targetDist) j++;
      const p1 = data[j];
      const p2 = data[Math.min(j + 1, data.length - 1)];
      
      if (p1.distance === p2.distance) {
        uniformData.push({ ...p1 });
      } else {
        const t = (targetDist - p1.distance) / (p2.distance - p1.distance);
        uniformData.push({
          distance: targetDist,
          elevation: p1.elevation + t * (p2.elevation - p1.elevation),
          steepness: 0, // Will be calculated after all points are generated
        });
      }
    }
    
    // Recalculate steepness based on the actual aggregated points
    for (let i = 0; i < uniformData.length; i++) {
      if (i === 0 || i === uniformData.length - 1) {
        // For first and last point, use adjacent point
        if (uniformData.length === 1) {
          uniformData[i].steepness = 0;
        } else if (i === 0) {
          const distDiff = uniformData[1].distance - uniformData[0].distance;
          const elevDiff = uniformData[1].elevation - uniformData[0].elevation;
          uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
        } else {
          const distDiff = uniformData[i].distance - uniformData[i - 1].distance;
          const elevDiff = uniformData[i].elevation - uniformData[i - 1].elevation;
          uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
        }
      } else {
        // For middle points, calculate from previous to next point for smoother gradient
        const distDiff = uniformData[i + 1].distance - uniformData[i - 1].distance;
        const elevDiff = uniformData[i + 1].elevation - uniformData[i - 1].elevation;
        uniformData[i].steepness = distDiff > 0 ? (elevDiff / (distDiff * 1000)) * 100 : 0;
      }
    }
    
    return uniformData;
  };

  // Calculate smart Y-axis tick intervals based on elevation range
  const calculateYAxisTicks = (minElevation: number, maxElevation: number) => {
    const range = maxElevation - minElevation;
    let step: number;
    
    if (range <= 100) {
      step = 20;
    } else if (range <= 250) {
      step = 50;
    } else if (range <= 500) {
      step = 100;
    } else if (range <= 1000) {
      step = 200;
    } else {
      step = 500;
    }
    
    // Round min/max to step boundaries
    const tickMin = Math.floor(minElevation / step) * step;
    const tickMax = Math.ceil(maxElevation / step) * step;
    
    // Generate tick array
    const ticks: number[] = [];
    for (let tick = tickMin; tick <= tickMax; tick += step) {
      ticks.push(tick);
    }
    
    return {
      ticks,
      domain: [tickMin, tickMax] as [number, number]
    };
  };

  // Use aggregated data for better performance and visual clarity
  // Data is already aggregated to 150 points before being passed to the chart
  // Only aggregate if data has more than 150 points (shouldn't happen normally)
  const uniformData = useMemo(() => {
    if (data.length <= 150) {
      return data; // Already aggregated, use as-is
    }
    return generateUniformData(data, 150);
  }, [data]);
  
  // Initialize zoom state with total distance range - use original data for exact distance
  const totalDistance = data.length > 0 ? data[data.length - 1].distance : 0;

  // Snap helper auch für Zoom/Pan verwenden
  const snapEndDistance = (end: number, total: number) => {
    if (Math.abs(end - total) < 0.05) return total;
    return end;
  };

  // Create stable references for callbacks to prevent infinite loops
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  // Stable data reference to prevent unnecessary re-renders
  const dataLengthRef = useRef(data.length);
  const dataHashRef = useRef('');
  
  // Create a stable hash of the data to detect real changes
  const currentDataHash = useMemo(() => {
    if (data.length === 0) return 'empty';
    return `${data.length}-${data[0]?.distance || 0}-${data[data.length - 1]?.distance || 0}`;
  }, [data]);
  
  // Update refs when data actually changes
  useEffect(() => {
    dataLengthRef.current = data.length;
    dataHashRef.current = currentDataHash;
  }, [currentDataHash]);

  // Update callback ref when it changes
  useEffect(() => {
    onHoverInfoChangeRef.current = onHoverInfoChange;
  }, [onHoverInfoChange]);

  // Update climbs ref when it changes
  useEffect(() => {
    climbsRef.current = climbs;
  }, [climbs]);

  // Update routeInfo ref when it changes
  useEffect(() => {
    routeInfoRef.current = routeInfo;
  }, [routeInfo]);

  // Reset zoom when route data changes (new route calculation)
  useEffect(() => {
    if (totalDistance > 0) {
      setZoomState({
        startDistance: 0,
        endDistance: totalDistance,
        isZoomed: false
      });
      // Also clear any active hover point when route changes
      onHover(null);
    }
  }, [currentDataHash, totalDistance, onHover]);

  // Simplified Sticky Point Manager - keep point at original location, just control visibility
  // Only run when zoom state changes, not when data changes
  useEffect(() => {
    // Skip sticky point management during zoom operations
    if (isZoomingRef.current) {
      return;
    }
    
    // No need to change activePoint - just let the ReferenceLine and activeDot 
    // visibility be controlled by their own range checks
    // The activePoint stays at its original distance location
  }, [stickyHover, activePoint, zoomState.startDistance, zoomState.endDistance]); // Removed data dependency

  // Filter data based on zoom state - optimized with memo
  const visibleData = useMemo(() => {
    if (!zoomState.isZoomed || uniformData.length === 0) {
      return uniformData;
    }
    
    // Snap endDistance auf totalDistance, wenn fast am Ende
    const snappedEnd = snapEndDistance(zoomState.endDistance, totalDistance);
    
    // If we have full elevation data, use it for re-aggregation (better quality)
    if (fullElevationData && fullElevationData.length > 150) {
      // Use binary search for better performance on large datasets
      let startIdx = 0;
      let endIdx = fullElevationData.length - 1;
      
      // Find start index
      let left = 0, right = fullElevationData.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (fullElevationData[mid].distance < zoomState.startDistance) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      startIdx = Math.max(0, left - 1); // Include one point before for smooth transition
      
      // Find end index
      left = 0;
      right = fullElevationData.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (fullElevationData[mid].distance <= snappedEnd) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      endIdx = Math.min(fullElevationData.length - 1, right + 1); // Include one point after for smooth transition
      
      // Extract the range using slice (much faster than filter)
      const filtered = fullElevationData.slice(startIdx, endIdx + 1);
      
      // Calculate the visible distance range
      const visibleDistance = snappedEnd - zoomState.startDistance;
      
      // Determine optimal number of points based on visible distance
      // Minimum 100m per point to prevent over-aggregation and jumpy steepness
      // Maximum 150 points for performance
      const minMetersPerPoint = 100; // Minimum 100m between points
      const maxPoints = Math.min(150, Math.max(30, Math.floor(visibleDistance * 1000 / minMetersPerPoint)));
      
      // Aggregate the filtered full-resolution data with dynamic point count
      if (filtered.length > maxPoints) {
        return aggregateElevationData(filtered, maxPoints);
      }
      
      return filtered.length >= 2 ? filtered : uniformData.slice(0, 2);
    }
    
    // Fallback: Filter the pre-aggregated data to the zoomed range
    const filtered = uniformData.filter(point =>
      point.distance >= zoomState.startDistance && 
      point.distance <= snappedEnd
    );
    
    // Re-aggregate to 150 points when we have less than 100 for smoother zoomed view
    if (filtered.length < 100 && filtered.length >= 2) {
      return generateUniformData(filtered, 150, zoomState.startDistance, snappedEnd);
    }
    
    // When panned to the end, ensure we include the exact end point with total distance
    if (Math.abs(snappedEnd - totalDistance) < 0.05 && uniformData.length > 0) {
      const lastOriginalPoint = uniformData[uniformData.length - 1];
      // If the last point is not exactly at total distance, create one
      if (Math.abs(lastOriginalPoint.distance - totalDistance) > 0.01) {
        const exactEndPoint = {
          distance: totalDistance,
          elevation: lastOriginalPoint.elevation,
          steepness: lastOriginalPoint.steepness
        };
        
        // Remove the old last point if it's very close and add the exact one
        const finalFiltered = filtered.filter(p => Math.abs(p.distance - totalDistance) > 0.01);
        return [...finalFiltered, exactEndPoint];
      }
    }
    
    return filtered.length >= 2 ? filtered : uniformData.slice(0, 2);
  }, [uniformData, zoomState.startDistance, zoomState.endDistance, zoomState.isZoomed, totalDistance, fullElevationData, highlightedClimbIndex]);

  // Create a stable hash of visibleData to avoid dependency issues
  const visibleDataHash = useMemo(() => {
    if (visibleData.length === 0) return 'empty';
    return `${visibleData.length}-${visibleData[0]?.distance.toFixed(3) || 0}-${visibleData[visibleData.length - 1]?.distance.toFixed(3) || 0}`;
  }, [visibleData]);
  
  // Convert coordinate index to elevation data index - MUST be after visibleData is defined
  useEffect(() => {
    // activePoint is now a COORDINATE index, we need to convert it to elevation data index
    if (activePoint !== null && activePoint !== undefined && routeInfoRef.current?.coordinates) {
      const coordinate = routeInfoRef.current.coordinates[activePoint];
      
      if (!coordinate || coordinate.distance === undefined) {
        setHoverDistance(null);
        return;
      }
      
      const targetDistance = coordinate.distance;
      
      // Find the closest point in visibleData for hover info display
      let foundIndex = 0;
      let minDiff = Math.abs(visibleData[0].distance - targetDistance);
      
      for (let i = 1; i < visibleData.length; i++) {
        const diff = Math.abs(visibleData[i].distance - targetDistance);
        if (diff < minDiff) {
          minDiff = diff;
          foundIndex = i;
        }
      }
      
      // Use the found point for visual display and hover info
      if (visibleData[foundIndex]) {
        // Find the closest point in visibleData to get the exact position for both line and bubble
        const elevationPoint = visibleData[foundIndex];
        
        // Use the actual data point's distance for the ReferenceLine to ensure perfect alignment
        setHoverDistance(elevationPoint.distance);
        
        // Also update hover info for sticky hover
        if (stickyHover && onHoverInfoChangeRef.current) {
          const point = elevationPoint;
          
          // Calculate visible range for segment sizing
          const visibleRange = zoomState.isZoomed 
            ? (zoomState.endDistance - zoomState.startDistance)
            : totalDistance;
          
          // Find the gradient segment that this point belongs to for consistent color/value matching
          let gradient = 0;
          const matchingSegment = gradientSegments.find(seg => {
            // Find which segment this distance falls into
            const segmentStart = seg.distance - (visibleRange / (gradientSegments.length > 0 ? gradientSegments.length * 2 : 300));
            const segmentEnd = seg.distance + (visibleRange / (gradientSegments.length > 0 ? gradientSegments.length * 2 : 300));
            return point.distance >= segmentStart && point.distance < segmentEnd;
          });
          
          if (matchingSegment) {
            // Use the segment's average steepness to match the color
            gradient = matchingSegment.avgSteepness;
          } else {
            // Fallback: calculate from adjacent points
            if (foundIndex > 0 && foundIndex < visibleData.length - 1) {
              const prevPoint = visibleData[foundIndex - 1];
              const nextPoint = visibleData[foundIndex + 1];
              const distanceDiff = nextPoint.distance - prevPoint.distance;
              const elevationDiff = nextPoint.elevation - prevPoint.elevation;
              gradient = distanceDiff > 0 ? (elevationDiff / (distanceDiff * 1000)) * 100 : 0;
            }
          }
          
          // Calculate time (same logic as in processHoverInteraction)
          let timeStr = '';
          
          if (routeInfoRef.current && routeInfoRef.current.distance > 0) {
            const totalDistance = Number(routeInfoRef.current.distance);
            const duration = Number(routeInfoRef.current.duration);
            const maxElevationDistance = Math.max(...visibleData.map(p => p.distance));
            
            if (duration > 0 && totalDistance > 0) {
              const progressRatio = Math.min(1, Math.max(0, point.distance / totalDistance));
              let timeAtPoint = progressRatio * duration;
              
              const alternativeProgressRatio = Math.min(1, Math.max(0, point.distance / maxElevationDistance));
              const alternativeTimeAtPoint = alternativeProgressRatio * duration;
              
              if (progressRatio < 0.001 && alternativeProgressRatio > 0.01) {
                timeAtPoint = alternativeTimeAtPoint;
              }
              
              const hours = Math.floor(timeAtPoint / 3600);
              const minutes = Math.floor((timeAtPoint % 3600) / 60);
              timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
            } else {
              const estimatedDuration = (totalDistance / 15) * 3600;
              const progressRatio = Math.min(1, Math.max(0, point.distance / totalDistance));
              const timeAtPoint = progressRatio * estimatedDuration;
              const hours = Math.floor(timeAtPoint / 3600);
              const minutes = Math.floor((timeAtPoint % 3600) / 60);
              timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
            }
          } else {
            const estimatedTotalDistance = Math.max(...visibleData.map(p => p.distance));
            if (estimatedTotalDistance > 0) {
              const estimatedDuration = (estimatedTotalDistance / 15) * 3600;
              const progressRatio = Math.min(1, Math.max(0, point.distance / estimatedTotalDistance));
              const timeAtPoint = progressRatio * estimatedDuration;
              const hours = Math.floor(timeAtPoint / 3600);
              const minutes = Math.floor((timeAtPoint % 3600) / 60);
              timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
            }
          }
        
          const hoverInfoObj = {
            distance: point.distance,
            time: timeStr,
            gradient: gradient
          };
        
          // Call onHoverInfoChange using ref to avoid dependency issues
          setTimeout(() => {
            onHoverInfoChangeRef.current?.(hoverInfoObj);
          }, 0);
        }
      }
    } else {
      // Clear if no active point
      setHoverDistance(null);
      
      // Also clear hover info if no sticky hover
      if (!stickyHover && onHoverInfoChangeRef.current) {
        setTimeout(() => {
          onHoverInfoChangeRef.current?.(null);
        }, 0);
      }
    }
  }, [activePoint, stickyHover, visibleDataHash]); // Use hash instead of visibleData, removed routeInfo to use ref
  
  // Calculate highlighted climb ranges for elevation chart
  const climbHighlightRanges = useMemo(() => {
    if (highlightedClimbIndex === null || highlightedClimbIndex === undefined || !climbsRef.current || !climbsRef.current[highlightedClimbIndex]) {
      return [];
    }

    const highlightedClimb = climbsRef.current[highlightedClimbIndex];
    const startDistance = highlightedClimb.startDistance;
    const endDistance = highlightedClimb.endDistance;

    // Use full data instead of visibleData to find elevation range
    // This ensures the highlight remains even when zoomed
    const climbPoints = data.filter(point => 
      point.distance >= startDistance && point.distance <= endDistance
    );

    if (climbPoints.length === 0) return [];

    const minElevation = Math.min(...climbPoints.map(p => p.elevation));
    const maxElevation = Math.max(...climbPoints.map(p => p.elevation));
    
    return [{
      startDistance,
      endDistance,
      minElevation,
      maxElevation,
      climbPoints // Include the actual climb points for area rendering
    }];
  }, [highlightedClimbIndex, data]); // Changed from visibleData to data, removed climbs to use ref
  
  // Auto-zoom to climb when highlighting a climb, zoom out when unhighlighting
  useEffect(() => {
    if (highlightedClimbIndex !== null && highlightedClimbIndex !== undefined && climbsRef.current && climbsRef.current[highlightedClimbIndex]) {
      const climb = climbsRef.current[highlightedClimbIndex];
      const climbDistance = climb.endDistance - climb.startDistance;
      
      // Define zoom constraints
      const MIN_ZOOM_WINDOW = 2.0; // Minimum 2km zoom window for meaningful context
      const MIN_CLIMB_FOR_ZOOM = 0.5; // Only zoom for climbs longer than 500m
      
      // Check if climb is too short to benefit from zooming
      if (climbDistance < MIN_CLIMB_FOR_ZOOM) {
        // For very short climbs, don't zoom but still highlight
        setZoomState({
          isZoomed: false,
          startDistance: 0,
          endDistance: totalDistance
        });
        return;
      }
      
      // Calculate zoom window based on climb length with intelligent constraints
      let targetZoomDistance;
      
      if (climbDistance > 15) {
        // For very long climbs (>15km), use minimal padding to show the full climb
        targetZoomDistance = climbDistance * 1.2; // 10% padding on each side
      } else if (climbDistance > 8) {
        // For long climbs (8-15km), use moderate padding
        targetZoomDistance = climbDistance * 1.4; // 20% padding on each side
      } else if (climbDistance > 3) {
        // For medium climbs (3-8km), use more generous padding
        targetZoomDistance = climbDistance / 0.7; // Climb takes 70%, 15% padding on each side
      } else {
        // For shorter climbs (<3km), use generous padding for context
        targetZoomDistance = climbDistance / 0.6; // Climb takes 60%, 20% padding on each side
      }
      
      // Ensure minimum zoom window
      targetZoomDistance = Math.max(MIN_ZOOM_WINDOW, targetZoomDistance);
      
      const centerDistance = (climb.startDistance + climb.endDistance) / 2;
      
      // Calculate start and end distances for the zoom window
      let zoomStartDistance = centerDistance - (targetZoomDistance / 2);
      let zoomEndDistance = centerDistance + (targetZoomDistance / 2);
      
      // Ensure zoom window doesn't go outside route bounds
      if (zoomStartDistance < 0) {
        const offset = -zoomStartDistance;
        zoomStartDistance = 0;
        zoomEndDistance = Math.min(totalDistance, zoomEndDistance + offset);
      } else if (zoomEndDistance > totalDistance) {
        const offset = zoomEndDistance - totalDistance;
        zoomEndDistance = totalDistance;
        zoomStartDistance = Math.max(0, zoomStartDistance - offset);
      }
      
      // Final check: if the zoom window would be 90%+ of the route, don't zoom
      const finalZoomDistance = zoomEndDistance - zoomStartDistance;
      if (finalZoomDistance >= totalDistance * 0.9) {
        setZoomState({
          isZoomed: false,
          startDistance: 0,
          endDistance: totalDistance
        });
        return;
      }
      
      // Apply zoom to focus on the climb
      setZoomState({
        isZoomed: true,
        startDistance: zoomStartDistance,
        endDistance: zoomEndDistance
      });
    } else if (highlightedClimbIndex === null || highlightedClimbIndex === undefined) {
      // Zoom out to show full route when no climb is highlighted
      setZoomState({
        isZoomed: false,
        startDistance: 0,
        endDistance: totalDistance
      });
    }
  }, [highlightedClimbIndex, totalDistance]); // Removed climbs to use ref
  
  // Berechne min/max Höhe mit Puffer auf Basis der aggregierten Daten
  const minElevation = uniformData.length > 0 ? Math.min(...uniformData.map(p => p.elevation)) : 0;
  const maxElevation = uniformData.length > 0 ? Math.max(...uniformData.map(p => p.elevation)) : 0;
  const elevationBuffer = (maxElevation - minElevation) * 0.15; // 15% buffer for better visual spacing

  // Calculate smart Y-axis ticks for consistent intervals
  const yAxisConfig = useMemo(() => {
    return calculateYAxisTicks(minElevation - elevationBuffer, maxElevation + elevationBuffer);
  }, [minElevation, maxElevation, elevationBuffer]);

  // Create stable gradient segments with fixed boundaries to prevent flickering
  const createStableGradientSegments = (data: ElevationPoint[], visibleStartDistance: number, visibleEndDistance: number, segmentSize: number) => {
    if (data.length === 0) return [];
    
    const segments = [];
    
    // Align segments to ABSOLUTE grid boundaries starting from 0, not relative to visible range
    // This ensures segments stay in the same positions when panning
    const startGrid = Math.floor(visibleStartDistance / segmentSize) * segmentSize;
    const endGrid = Math.ceil(visibleEndDistance / segmentSize) * segmentSize;
    
    // Calculate visible range ONCE at the start - this is the x-axis range shown in the chart
    const visibleRange = visibleEndDistance - visibleStartDistance;
    
    for (let gridDistance = startGrid; gridDistance < endGrid; gridDistance += segmentSize) {
      const segmentStart = gridDistance;
      const segmentEnd = gridDistance + segmentSize;
      
      // Only process segments that overlap with visible range
      if (segmentEnd <= visibleStartDistance || segmentStart >= visibleEndDistance) {
        continue;
      }
      
      // Find all points within this grid segment
      const segmentPoints = data.filter(point => 
        point.distance >= segmentStart && point.distance < segmentEnd
      );
      
      if (segmentPoints.length > 0) {
        // Calculate average steepness for this segment (more stable than max with noisy data)
        const avgSteepness = segmentPoints.reduce((sum, point) => sum + point.steepness, 0) / segmentPoints.length;
        
        // Use the segment START distance as the position (not middle)
        // This aligns better with how the gradient is applied (from left to right)
        const segmentPosition = segmentStart;
        
        // Calculate offset as percentage of where this segment starts within the visible x-axis
        // Since the chart x-axis goes from visibleStartDistance to visibleEndDistance,
        // we calculate where segmentPosition falls as a percentage
        const offset = ((segmentPosition - visibleStartDistance) / visibleRange) * 100;
        
        segments.push({
          distance: segmentPosition,
          avgSteepness,
          offset: Math.max(0, Math.min(100, offset)) // Clamp to 0-100%
        });
      }
    }
    
    return segments;
  };

  // Calculate gradient segments based on visible data range with stable boundaries
  // Use uniformData for stable colors - recalculate on zoom changes but accept minor pan shifts
  const gradientSegments = useMemo(() => {
    // Calculate segment size ONCE based on the zoom state
    const visibleRange = zoomState.isZoomed 
      ? (zoomState.endDistance - zoomState.startDistance)
      : totalDistance;
    
    // Use consistent segment count for smooth coloring
    const targetSegmentCount = 150;
    const minSegmentSize = 0.15; // km (150m) - balanced detail level
    const stableSegmentSize = Math.max(minSegmentSize, visibleRange / targetSegmentCount);
    
    // When zoomed in, use fullElevationData if available for more detail
    // When zoomed out, use uniformData for overview
    const sourceData = (zoomState.isZoomed && fullElevationData && fullElevationData.length > uniformData.length)
      ? fullElevationData
      : uniformData;
    
    // Calculate gradients for the visible range
    if (!zoomState.isZoomed) {
      return createStableGradientSegments(sourceData, 0, totalDistance, stableSegmentSize);
    } else {
      return createStableGradientSegments(sourceData, zoomState.startDistance, zoomState.endDistance, stableSegmentSize);
    }
  }, [uniformData, fullElevationData, zoomState.startDistance, zoomState.endDistance, zoomState.isZoomed, totalDistance]);

  // Helper functions for zoom and touch handling
  const resetZoom = () => {
    setZoomState({
      startDistance: 0,
      endDistance: totalDistance,
      isZoomed: false
    });
  };

  // Double click to reset zoom and clear sticky hover
  const handleDoubleClick = () => {
    resetZoom();
    if (stickyHover && onClearStickyHover) {
      onClearStickyHover();
    }
  };

  // Overview bar drag handlers with optimized performance
  const updateZoomWithThrottle = (newStart: number, newEnd: number) => {
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      setZoomState({
        startDistance: newStart,
        endDistance: newEnd,
        isZoomed: newStart > 0.01 || newEnd < (totalDistance - 0.01)
      });
    });
  };

  const handleOverviewMouseDown = (e: React.MouseEvent) => {
    // Check if the click was on an edge handle
    const target = e.target as HTMLElement;
    if (target.classList.contains('overview-edge-handle') || 
        target.closest('.overview-edge-handle')) {
      // Let the edge handles handle their own events
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to hide activeDot during overview pan
    setHideActiveDot(true);
    
    overviewDragRef.current = {
      isDragging: true,
      startX: e.clientX,
      lastUpdateTime: performance.now()
    };
    // Add dragging class to disable transitions
    if (overviewElementRef.current) {
      overviewElementRef.current.classList.add('dragging');
    }
  };

  const handleOverviewTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Check if the touch was on an edge handle
      const target = e.target as HTMLElement;
      if (target.classList.contains('overview-edge-handle') || 
          target.closest('.overview-edge-handle')) {
        // Let the edge handles handle their own events
        return;
      }
      
      // Only prevent default if we can (not in a passive listener)
      try {
        e.preventDefault();
        e.stopPropagation();
      } catch (err) {
        // Silently handle passive listener case
      }
      
      // Set flag to hide activeDot during overview pan
      setHideActiveDot(true);
      
      overviewDragRef.current = {
        isDragging: true,
        startX: e.touches[0].clientX,
        lastUpdateTime: performance.now()
      };
      // Add dragging class to disable transitions
      if (overviewElementRef.current) {
        overviewElementRef.current.classList.add('dragging');
      }
    }
  };

  // Edge handle drag refs
  const handleLeftEdgeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to hide activeDot during edge drag
    setHideActiveDot(true);
    
    leftEdgeDragRef.current = {
      isDragging: true,
      startX: e.clientX,
      initialStartDistance: zoomState.startDistance,
      lastUpdateTime: performance.now()
    };
    // Add dragging class to disable transitions
    if (overviewElementRef.current) {
      overviewElementRef.current.classList.add('dragging');
    }
  };

  const handleLeftEdgeTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Only prevent default if we can (not in a passive listener)
      try {
        e.preventDefault();
        e.stopPropagation();
      } catch (err) {
        // Silently handle passive listener case
      }
      
      // Set flag to hide activeDot during edge drag
      setHideActiveDot(true);
      
      leftEdgeDragRef.current = {
        isDragging: true,
        startX: e.touches[0].clientX,
        initialStartDistance: zoomState.startDistance,
        lastUpdateTime: performance.now()
      };
      // Add dragging class to disable transitions
      if (overviewElementRef.current) {
        overviewElementRef.current.classList.add('dragging');
      }
    }
  };

  const handleRightEdgeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set flag to hide activeDot during edge drag
    setHideActiveDot(true);
    
    rightEdgeDragRef.current = {
      isDragging: true,
      startX: e.clientX,
      initialEndDistance: zoomState.endDistance,
      lastUpdateTime: performance.now()
    };
    // Add dragging class to disable transitions
    if (overviewElementRef.current) {
      overviewElementRef.current.classList.add('dragging');
    }
  };

  const handleRightEdgeTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      // Only prevent default if we can (not in a passive listener)
      try {
        e.preventDefault();
        e.stopPropagation();
      } catch (err) {
        // Silently handle passive listener case
      }
      
      // Set flag to hide activeDot during edge drag
      setHideActiveDot(true);
      
      rightEdgeDragRef.current = {
        isDragging: true,
        startX: e.touches[0].clientX,
        initialEndDistance: zoomState.endDistance,
        lastUpdateTime: performance.now()
      };
      // Add dragging class to disable transitions
      if (overviewElementRef.current) {
        overviewElementRef.current.classList.add('dragging');
      }
    }
  };

  // Mouse wheel zoom using native event listener to avoid passive event issues
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only zoom when Ctrl/Cmd key is held (standard zoom behavior)
      // This allows normal scrolling when modifier key is not pressed
      if (!e.ctrlKey && !e.metaKey) {
        return; // Allow normal scrolling
      }
      
      e.preventDefault();
      e.stopPropagation();
      
      // Set flag to hide activeDot during wheel zoom
      isZoomingRef.current = true;
      setHideActiveDot(true);
      
      const zoomFactor = e.deltaY > 0 ? 1.2 : 0.8;
      const currentRange = zoomState.endDistance - zoomState.startDistance;
      const newRange = Math.min(totalDistance, Math.max(totalDistance * 0.1, currentRange * zoomFactor));
      
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mousePercent = mouseX / rect.width;
      
      const centerDistance = zoomState.startDistance + (zoomState.endDistance - zoomState.startDistance) * mousePercent;
      const newStart = Math.max(0, centerDistance - newRange / 2);
      let newEnd = Math.min(totalDistance, centerDistance + newRange / 2);
      newEnd = snapEndDistance(newEnd, totalDistance); // <-- Snap hier
      
      setZoomState({
        startDistance: newStart,
        endDistance: newEnd,
        isZoomed: newStart > 0 || newEnd < totalDistance
      });
      
      // Clear zooming flag after a short delay
      setTimeout(() => {
        isZoomingRef.current = false;
      }, 100);
    };

    // Add non-passive wheel event listener
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [zoomState.startDistance, zoomState.endDistance, totalDistance]);

  // Global mouse/touch listeners for overview bar dragging with performance optimization
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      const container = chartContainerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const overviewWidth = rect.width - 70; // Account for left/right margins
      
      // Handle left edge dragging
      if (leftEdgeDragRef.current?.isDragging) {
        const now = performance.now();
        if (now - leftEdgeDragRef.current.lastUpdateTime < 16) return; // ~60fps
        leftEdgeDragRef.current.lastUpdateTime = now;
        
        const deltaX = e.clientX - leftEdgeDragRef.current.startX;
        const deltaPercent = deltaX / overviewWidth;
        const deltaDistance = deltaPercent * totalDistance;
        
        const newStart = Math.max(0, Math.min(zoomState.endDistance - (totalDistance * 0.05), leftEdgeDragRef.current.initialStartDistance + deltaDistance));
        
        if (newStart >= 0 && newStart < zoomState.endDistance) {
          updateZoomWithThrottle(newStart, zoomState.endDistance);
        }
        return;
      }
      
      // Handle right edge dragging
      if (rightEdgeDragRef.current?.isDragging) {
        const now = performance.now();
        if (now - rightEdgeDragRef.current.lastUpdateTime < 16) return; // ~60fps
        rightEdgeDragRef.current.lastUpdateTime = now;
        
        const deltaX = e.clientX - rightEdgeDragRef.current.startX;
        const deltaPercent = deltaX / overviewWidth;
        const deltaDistance = deltaPercent * totalDistance;
        
        const newEnd = Math.min(totalDistance, Math.max(zoomState.startDistance + (totalDistance * 0.05), rightEdgeDragRef.current.initialEndDistance + deltaDistance));
        const snappedEnd = snapEndDistance(newEnd, totalDistance);
        
        if (snappedEnd <= totalDistance && snappedEnd > zoomState.startDistance) {
          updateZoomWithThrottle(zoomState.startDistance, snappedEnd);
        }
        return;
      }
      
      // Handle overview bar dragging (original logic)
      if (!overviewDragRef.current?.isDragging) return;
      
      // Throttle updates to improve performance
      const now = performance.now();
      if (now - overviewDragRef.current.lastUpdateTime < 16) return; // ~60fps
      overviewDragRef.current.lastUpdateTime = now;
      
      const deltaX = e.clientX - overviewDragRef.current.startX;
      const panPercent = deltaX / overviewWidth;
      const currentRange = zoomState.endDistance - zoomState.startDistance;
      const panDistance = panPercent * totalDistance;
      
      // Calculate new position
      const newStart = Math.max(0, Math.min(totalDistance - currentRange, zoomState.startDistance + panDistance));
      const newEnd = snapEndDistance(newStart + currentRange, totalDistance);
      
      if (newEnd <= totalDistance && newStart >= 0) {
        updateZoomWithThrottle(newStart, newEnd);
      }
      
      overviewDragRef.current.startX = e.clientX;
    };

    const handleGlobalMouseUp = () => {
      // Clean up left edge dragging
      if (leftEdgeDragRef.current?.isDragging) {
        if (leftEdgeDragRef.current.animationFrameId) {
          cancelAnimationFrame(leftEdgeDragRef.current.animationFrameId);
        }
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
        leftEdgeDragRef.current = null;
        return;
      }
      
      // Clean up right edge dragging
      if (rightEdgeDragRef.current?.isDragging) {
        if (rightEdgeDragRef.current.animationFrameId) {
          cancelAnimationFrame(rightEdgeDragRef.current.animationFrameId);
        }
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
        rightEdgeDragRef.current = null;
        return;
      }
      
      // Clean up overview bar dragging (original logic)
      if (overviewDragRef.current?.isDragging) {
        // Cancel any pending animation frame
        if (overviewDragRef.current.animationFrameId) {
          cancelAnimationFrame(overviewDragRef.current.animationFrameId);
        }
        // Remove dragging class to re-enable transitions
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
      }
      overviewDragRef.current = null;
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      
      const container = chartContainerRef.current;
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const overviewWidth = rect.width - 70; // Account for left/right margins
      
      // Handle left edge touch dragging
      if (leftEdgeDragRef.current?.isDragging) {
        const now = performance.now();
        if (now - leftEdgeDragRef.current.lastUpdateTime < 16) return; // ~60fps
        leftEdgeDragRef.current.lastUpdateTime = now;
        
        const deltaX = e.touches[0].clientX - leftEdgeDragRef.current.startX;
        const deltaPercent = deltaX / overviewWidth;
        const deltaDistance = deltaPercent * totalDistance;
        
        const newStart = Math.max(0, Math.min(zoomState.endDistance - (totalDistance * 0.05), leftEdgeDragRef.current.initialStartDistance + deltaDistance));
        
        if (newStart >= 0 && newStart < zoomState.endDistance) {
          updateZoomWithThrottle(newStart, zoomState.endDistance);
        }
        return;
      }
      
      // Handle right edge touch dragging
      if (rightEdgeDragRef.current?.isDragging) {
        const now = performance.now();
        if (now - rightEdgeDragRef.current.lastUpdateTime < 16) return; // ~60fps
        rightEdgeDragRef.current.lastUpdateTime = now;
        
        const deltaX = e.touches[0].clientX - rightEdgeDragRef.current.startX;
        const deltaPercent = deltaX / overviewWidth;
        const deltaDistance = deltaPercent * totalDistance;
        
        const newEnd = Math.min(totalDistance, Math.max(zoomState.startDistance + (totalDistance * 0.05), rightEdgeDragRef.current.initialEndDistance + deltaDistance));
        const snappedEnd = snapEndDistance(newEnd, totalDistance);
        
        if (snappedEnd <= totalDistance && snappedEnd > zoomState.startDistance) {
          updateZoomWithThrottle(zoomState.startDistance, snappedEnd);
        }
        return;
      }
      
      // Handle overview bar touch dragging (original logic)
      if (!overviewDragRef.current?.isDragging) return;
      
      // Throttle updates to improve performance
      const now = performance.now();
      if (now - overviewDragRef.current.lastUpdateTime < 16) return; // ~60fps
      overviewDragRef.current.lastUpdateTime = now;
      
      const deltaX = e.touches[0].clientX - overviewDragRef.current.startX;
      const panPercent = deltaX / overviewWidth;
      const currentRange = zoomState.endDistance - zoomState.startDistance;
      const panDistance = panPercent * totalDistance;
      
      // Calculate new position
      const newStart = Math.max(0, Math.min(totalDistance - currentRange, zoomState.startDistance + panDistance));
      const newEnd = snapEndDistance(newStart + currentRange, totalDistance);
      
      if (newEnd <= totalDistance && newStart >= 0) {
        updateZoomWithThrottle(newStart, newEnd);
      }
      
      overviewDragRef.current.startX = e.touches[0].clientX;
    };

    const handleGlobalTouchEnd = () => {
      // Clean up left edge touch dragging
      if (leftEdgeDragRef.current?.isDragging) {
        if (leftEdgeDragRef.current.animationFrameId) {
          cancelAnimationFrame(leftEdgeDragRef.current.animationFrameId);
        }
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
        leftEdgeDragRef.current = null;
        return;
      }
      
      // Clean up right edge touch dragging
      if (rightEdgeDragRef.current?.isDragging) {
        if (rightEdgeDragRef.current.animationFrameId) {
          cancelAnimationFrame(rightEdgeDragRef.current.animationFrameId);
        }
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
        rightEdgeDragRef.current = null;
        return;
      }
      
      // Clean up overview bar touch dragging (original logic)
      if (overviewDragRef.current?.isDragging) {
        // Cancel any pending animation frame
        if (overviewDragRef.current.animationFrameId) {
          cancelAnimationFrame(overviewDragRef.current.animationFrameId);
        }
        // Remove dragging class to re-enable transitions
        if (overviewElementRef.current) {
          overviewElementRef.current.classList.remove('dragging');
        }
      }
      overviewDragRef.current = null;
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      // Clean up any pending animation frames
      if (overviewDragRef.current?.animationFrameId) {
        cancelAnimationFrame(overviewDragRef.current.animationFrameId);
      }
      if (leftEdgeDragRef.current?.animationFrameId) {
        cancelAnimationFrame(leftEdgeDragRef.current.animationFrameId);
      }
      if (rightEdgeDragRef.current?.animationFrameId) {
        cancelAnimationFrame(rightEdgeDragRef.current.animationFrameId);
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [zoomState.startDistance, zoomState.endDistance, totalDistance, updateZoomWithThrottle]);

  // Simpler approach - just listen for document touches/clicks but only when actively hovering
  useEffect(() => {
    const handleDocumentTouch = (e: TouchEvent | MouseEvent) => {
      // Check if the touch/click is outside the chart container
      const chartContainer = document.querySelector('.elevation-chart-container');
      const target = e.target as Node;
      
      // Exclude zoom control buttons from clearing hover
      const isZoomButton = target && (target as Element).closest && (
        (target as Element).closest('.zoom-controls') ||
        (target as Element).classList?.contains('zoom-button') ||
        (target as Element).closest('[class*="zoom"]') ||
        (target as Element).closest('button[class*="MuiIconButton"]') ||
        (target as Element).tagName === 'svg' ||
        (target as Element).closest('svg')
      );
      
      if (chartContainer && !chartContainer.contains(target) && !isZoomButton) {
        // Only clear hover if sticky hover is disabled
        if (!stickyHover) {
          onHover(null);
        }
      }
    };

    document.addEventListener('touchstart', handleDocumentTouch);
    document.addEventListener('mousedown', handleDocumentTouch);

    return () => {
      document.removeEventListener('touchstart', handleDocumentTouch);
      document.removeEventListener('mousedown', handleDocumentTouch);
    };
  }, [onHover, stickyHover]);

  // Helper function to snap zoom level to nearest 10% step
  const snapZoomToStep = (currentVisiblePercentage: number): number => {
    const currentZoomLevel = (1 - currentVisiblePercentage) * 100;
    // Round to nearest 10% step
    return Math.round(currentZoomLevel / 10) * 10;
  };

  // Zoom control functions with smart centering around active point
  const handleZoomIn = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.stopPropagation();
    // Only prevent default if we can (not in a passive listener)
    try {
      e?.preventDefault();
    } catch (err) {
      // Silently handle passive listener case
    }
    if (totalDistance === 0) return;
    
    // Set flag to hide activeDot during zoom
    isZoomingRef.current = true;
    setHideActiveDot(true);
    
    // Calculate current visible percentage and snap to nearest 10% step
    const currentVisiblePercentage = (zoomState.endDistance - zoomState.startDistance) / totalDistance;
    const snappedZoomLevel = snapZoomToStep(currentVisiblePercentage);
    
    // Increase zoom by exactly 10 percentage points from snapped level, max 90%
    const newZoomLevel = Math.min(90, snappedZoomLevel + 10);
    const newVisiblePercentage = (100 - newZoomLevel) / 100;
    const newRange = totalDistance * newVisiblePercentage;
    
    // Smart centering: use hover distance if available, otherwise current center
    let center = (zoomState.startDistance + zoomState.endDistance) / 2;
    
    if (hoverDistance !== null && hoverDistance !== undefined) {
      // Center around the hover point distance
      center = hoverDistance;
    }
    
    const newStart = Math.max(0, center - newRange / 2);
    let newEnd = Math.min(totalDistance, center + newRange / 2);
    
    // If centering would push beyond bounds, adjust both start and end
    if (newEnd >= totalDistance) {
      newEnd = totalDistance;
      const adjustedStart = Math.max(0, newEnd - newRange);
      setZoomState({
        startDistance: adjustedStart,
        endDistance: newEnd,
        isZoomed: adjustedStart > 0.01 || newEnd < (totalDistance - 0.01)
      });
    } else if (newStart <= 0) {
      const adjustedEnd = Math.min(totalDistance, newRange);
      setZoomState({
        startDistance: 0,
        endDistance: adjustedEnd,
        isZoomed: adjustedEnd < (totalDistance - 0.01)
      });
    } else {
      setZoomState({
        startDistance: newStart,
        endDistance: newEnd,
        isZoomed: newStart > 0.01 || newEnd < (totalDistance - 0.01)
      });
    }
    
    // Clear zooming flag after a short delay
    setTimeout(() => {
      isZoomingRef.current = false;
    }, 100);
  };

  const handleZoomOut = (e?: React.MouseEvent | React.TouchEvent) => {
    e?.stopPropagation();
    // Only prevent default if we can (not in a passive listener)
    try {
      e?.preventDefault();
    } catch (err) {
      // Silently handle passive listener case
    }
    if (totalDistance === 0) return;
    
    // Set flag to hide activeDot during zoom
    isZoomingRef.current = true;
    setHideActiveDot(true);
    
    // Calculate current visible percentage and snap to nearest 10% step
    const currentVisiblePercentage = (zoomState.endDistance - zoomState.startDistance) / totalDistance;
    const snappedZoomLevel = snapZoomToStep(currentVisiblePercentage);
    
    // Decrease zoom by exactly 10 percentage points from snapped level, min 0%
    const newZoomLevel = Math.max(0, snappedZoomLevel - 10);
    
    if (newZoomLevel <= 0) {
      // Reset to full view when zoom level reaches 0 or below
      setZoomState({
        startDistance: 0,
        endDistance: totalDistance,
        isZoomed: false
      });
    } else {
      const newVisiblePercentage = (100 - newZoomLevel) / 100;
      const newRange = totalDistance * newVisiblePercentage;
      
      // Smart centering: use hover distance if available, otherwise current center
      let center = (zoomState.startDistance + zoomState.endDistance) / 2;
      
      if (hoverDistance !== null && hoverDistance !== undefined) {
        // Center around the hover point distance
        center = hoverDistance;
      }
      
      const newStart = Math.max(0, center - newRange / 2);
      let newEnd = Math.min(totalDistance, center + newRange / 2);
      
      // If centering would push beyond bounds, adjust both start and end
      if (newEnd >= totalDistance) {
        newEnd = totalDistance;
        const adjustedStart = Math.max(0, newEnd - newRange);
        setZoomState({
          startDistance: adjustedStart,
          endDistance: newEnd,
          isZoomed: adjustedStart > 0.01 || newEnd < (totalDistance - 0.01)
        });
      } else if (newStart <= 0) {
        const adjustedEnd = Math.min(totalDistance, newRange);
        setZoomState({
          startDistance: 0,
          endDistance: adjustedEnd,
          isZoomed: adjustedEnd < (totalDistance - 0.01)
        });
      } else {
        setZoomState({
          startDistance: newStart,
          endDistance: newEnd,
          isZoomed: newStart > 0.01 || newEnd < (totalDistance - 0.01)
        });
      }
    }
    
    // Clear zooming flag after a short delay
    setTimeout(() => {
      isZoomingRef.current = false;
    }, 100);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Only clear hover info if sticky hover is disabled
    if (!stickyHover) {
      onHoverInfoChange?.(null);
      onHover(null);
    }
    // When sticky hover is enabled, keep everything visible (line, info, map sync)
  };

  const handleMouseMove = (state: any) => {
    // Throttle mouse move events for performance
    const now = performance.now();
    if (now - lastMouseMoveRef.current < MOUSE_MOVE_THROTTLE_MS) {
      return;
    }
    lastMouseMoveRef.current = now;
    
    // Ignore hover events during multi-touch (pinch/zoom)
    if (isMultiTouchRef.current) {
      return;
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Re-enable activeDot when user hovers again after zoom
    if (hideActiveDot && state && state.activeTooltipIndex !== undefined) {
      setHideActiveDot(false);
    }
    
    // Always process hover interactions - sticky hover shouldn't prevent normal hover
    if (state && state.activeTooltipIndex !== undefined) {
      processHoverInteraction(state.activeTooltipIndex);
    } else {
      // Only clear hover info if sticky hover is disabled
      if (!stickyHover) {
        onHoverInfoChange?.(null);
        onHover(null);
      }
      // When sticky hover is enabled, keep everything visible (line, info, map sync)
    }
  };

  // Handle click/touch interactions on the chart
  const handleChartClick = (state: any) => {
    // Ignore click events during or immediately after multi-touch (pinch/zoom)
    if (isMultiTouchRef.current) {
      return;
    }
    
    // Re-enable activeDot when user clicks after zoom
    if (hideActiveDot && state && state.activeTooltipIndex !== undefined) {
      setHideActiveDot(false);
    }
    
    if (state && state.activeTooltipIndex !== undefined) {
      processHoverInteraction(state.activeTooltipIndex);
    }
  };

  // Common function to process hover/click interactions
  const processHoverInteraction = (activeTooltipIndex: number) => {
    // Map visible data index back to original data index
    const visiblePoint = visibleData[activeTooltipIndex];
    if (visiblePoint) {
      const originalIndex = data.findIndex((point, index) => {
        const nextPoint = data[index + 1];
        return point.distance <= visiblePoint.distance && 
               (!nextPoint || nextPoint.distance > visiblePoint.distance);
      });
      
      // Calculate gradient for this point
      let gradient = 0;
      if (originalIndex > 0 && originalIndex < data.length - 1) {
        const prevPoint = data[originalIndex - 1];
        const nextPoint = data[originalIndex + 1];
        const distanceDiff = nextPoint.distance - prevPoint.distance;
        const elevationDiff = nextPoint.elevation - prevPoint.elevation;
        gradient = distanceDiff > 0 ? (elevationDiff / (distanceDiff * 1000)) * 100 : 0; // Convert to percentage
      }
      
      // NEW: Find the corresponding coordinate index based on distance
      let coordinateIndex = originalIndex; // Fallback to elevation data index
      
      if (routeInfo?.coordinates && routeInfo.coordinates.length > 0) {
        // Search through coordinates array for the closest match by distance
        const targetDistance = visiblePoint.distance;
        let minDiff = Infinity;
        
        for (let i = 0; i < routeInfo.coordinates.length; i++) {
          const coord = routeInfo.coordinates[i];
          if (coord.distance !== undefined) {
            const diff = Math.abs(coord.distance - targetDistance);
            if (diff < minDiff) {
              minDiff = diff;
              coordinateIndex = i;
            }
          }
        }
      }
      
      // Calculate time (assuming constant speed based on route info)
      let timeStr = '';
      
      if (routeInfo && routeInfo.distance > 0) {
        // Convert string values to numbers (API sometimes returns strings)
        const totalDistance = Number(routeInfo.distance);
        const duration = Number(routeInfo.duration); // Duration is already in seconds from API
        
        // Also check what the actual max distance in the elevation data is
        const maxElevationDistance = Math.max(...data.map(p => p.distance));
        
        if (duration > 0 && totalDistance > 0) {
          // Calculate progress ratio: how far along the route are we?
          const progressRatio = Math.min(1, Math.max(0, visiblePoint.distance / totalDistance));
          let timeAtPoint = progressRatio * duration;
          
          // Try alternative calculation using elevation data max distance if needed
          const alternativeProgressRatio = Math.min(1, Math.max(0, visiblePoint.distance / maxElevationDistance));
          const alternativeTimeAtPoint = alternativeProgressRatio * duration;
          
          // Use the elevation data max distance if it seems more reasonable
          if (progressRatio < 0.001 && alternativeProgressRatio > 0.01) {
            timeAtPoint = alternativeTimeAtPoint;
          }
          
          const hours = Math.floor(timeAtPoint / 3600);
          const minutes = Math.floor((timeAtPoint % 3600) / 60);
          timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
        } else {
          // Fallback: estimate based on 15 km/h average speed
          const estimatedDuration = (totalDistance / 15) * 3600;
          const progressRatio = Math.min(1, Math.max(0, visiblePoint.distance / totalDistance));
          const timeAtPoint = progressRatio * estimatedDuration;
          const hours = Math.floor(timeAtPoint / 3600);
          const minutes = Math.floor((timeAtPoint % 3600) / 60);
          timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
          
        }
      } else {
        // Fallback: Just use a simple estimation if no routeInfo
        const estimatedTotalDistance = Math.max(...data.map(p => p.distance));
        if (estimatedTotalDistance > 0) {
          const estimatedDuration = (estimatedTotalDistance / 15) * 3600; // 15 km/h
          const progressRatio = Math.min(1, Math.max(0, visiblePoint.distance / estimatedTotalDistance));
          const timeAtPoint = progressRatio * estimatedDuration;
          const hours = Math.floor(timeAtPoint / 3600);
          const minutes = Math.floor((timeAtPoint % 3600) / 60);
          timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;
        }
      }
      
      const hoverInfoObj = {
        distance: visiblePoint.distance,
        time: timeStr,
        gradient: gradient
      };
      
      onHoverInfoChange?.(hoverInfoObj);
      
      // Use coordinateIndex instead of originalIndex for map marker
      onHover(coordinateIndex >= 0 ? coordinateIndex : null);
    } else {
      // Only clear hover info if sticky hover is disabled
      if (!stickyHover) {
        onHoverInfoChange?.(null);
        onHover(null);
      }
      // When sticky hover is enabled, keep everything visible (line, info, map sync)
    }
  };

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Cancel any pending animation frames on component unmount
      if (overviewDragRef.current?.animationFrameId) {
        cancelAnimationFrame(overviewDragRef.current.animationFrameId);
      }
    };
  }, []);

  // Ticks für die X-Achse basierend auf ganzen Kilometern für bessere Lesbarkeit
  const numTicks = 8; // Erhöhe auf 8 für bessere Abdeckung
  const ticks = useMemo(() => {
    if (visibleData.length < 2) return [];
    
    const minDistance = visibleData[0].distance;
    let maxDistance = visibleData[visibleData.length - 1].distance;
    
    // When zoomed and panned to the end, ensure the max tick reaches the total route distance
    if (zoomState.isZoomed && totalDistance > 0) {
      const snappedEnd = snapEndDistance(zoomState.endDistance, totalDistance);
      // If we're very close to the end of the route, use the total distance for ticks
      if (Math.abs(snappedEnd - totalDistance) < 0.05) {
        maxDistance = totalDistance;
      } else {
        maxDistance = snappedEnd;
      }
    }
    
    const distanceRange = maxDistance - minDistance;
    
    if (distanceRange === 0) return [Math.round(minDistance)];
    
    // Bestimme einen sinnvollen Schritt für ganze Kilometer basierend auf dem Bereich
    const idealStep = distanceRange / (numTicks - 1);
    let step;
    
    if (idealStep <= 0.5) {
      step = 0.5; // 500m Schritte bei sehr kleinen Bereichen
    } else if (idealStep <= 1) {
      step = 1; // 1km Schritte
    } else if (idealStep <= 2) {
      step = 2; // 2km Schritte
    } else if (idealStep <= 3) {
      step = 3; // 3km Schritte
    } else if (idealStep <= 5) {
      step = 5; // 5km Schritte
    } else {
      step = Math.ceil(idealStep / 5) * 5; // 5er-Schritte, aufgerundet
    }
    
    // Starte beim ersten ganzen Schritt >= minDistance
    const startTick = Math.ceil(minDistance / step) * step;
    const ticks = [];
    
    // Generiere alle Ticks bis zum Ende
    for (let tick = startTick; tick <= maxDistance; tick += step) {
      if (tick >= minDistance) {
        ticks.push(Math.round(tick * 10) / 10); // Runde auf 1 Dezimalstelle
      }
    }
    
    // Ensure the last tick is exactly the total distance when at the end
    if (zoomState.isZoomed && totalDistance > 0 && Math.abs(maxDistance - totalDistance) < 0.05) {
      // Remove any tick that's very close to the total distance
      const filteredTicks = ticks.filter(tick => Math.abs(tick - totalDistance) > 0.05);
      // Add the exact total distance as the last tick
      filteredTicks.push(totalDistance);
      return filteredTicks;
    }
    
    return ticks;
  }, [visibleData, zoomState.isZoomed, zoomState.endDistance, totalDistance]);

  return (
    <Paper 
      variant={variant === 'detail' ? 'elevation' : 'outlined'}
      className={`elevation-profile-paper${variant === 'detail' ? ' detail-panel' : ''}`}
      sx={variant === 'detail' ? { 
        border: 'none', 
        boxShadow: 'none',
        marginTop: 0,
        paddingTop: 0
      } : {
        marginTop: 0,
        paddingTop: 0
      }}
    >
      
      {data.length === 0 ? (
        <div style={{ 
          height: variant === 'detail' ? '220px' : '240px', // Reduced to fit in DetailPanel
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: '#666',
          fontSize: '14px'
        }}>
          Keine Höhendaten verfügbar
        </div>
      ) : (
      <div 
        ref={chartContainerRef}
        className={`elevation-chart-container${hideActiveDot ? ' hide-active-dot' : ''}`}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseLeave}
        onDoubleClick={handleDoubleClick}
        style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}
      >
        {/* Chart-Bereich mit maximaler Breite */}
        <div style={{ flex: '1 1 0', minWidth: 0, width: '100%', touchAction: 'pan-y' }}>
          <ResponsiveContainer 
            width="100%"
            height={variant === 'detail' ? 220 : "100%"} // Reduced to fit in DetailPanel
          >
            <ComposedChart
              data={visibleData}
              margin={{
                top: 10, // Reduced from 20 to save space
                right: 10,
                left: 0,
                bottom: 50, // Reduced from 75 to save space
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseLeave}
              onClick={handleChartClick}
              style={{ overflow: 'hidden' }}
            >
              <CartesianGrid strokeDasharray="3 3" className="elevation-grid" />
              <XAxis 
                dataKey="distance"
                tickFormatter={(value: number) => {
                  // Zeige ganze Kilometer oder halbe Kilometer ohne Dezimalstellen
                  if (value % 1 === 0) {
                    return `${Math.round(value)}km`;
                  } else if (value % 0.5 === 0) {
                    return `${value}km`;
                  } else {
                    return `${Math.round(value * 10) / 10}km`;
                  }
                }}
                tick={{ fontSize: 10, className: 'elevation-axis-tick' }}
                axisLine={{ className: 'elevation-axis-line' }}
                tickLine={{ className: 'elevation-tick-line' }}
                interval={0}
                domain={[
                  visibleData.length > 0 ? visibleData[0].distance : 0,
                  visibleData.length > 0 ? visibleData[visibleData.length - 1].distance : 0
                ]}
                type="number"
                scale="linear"
                ticks={ticks}
              />
              <YAxis 
                dataKey="elevation"
                domain={yAxisConfig.domain}
                ticks={yAxisConfig.ticks}
                tick={{ fontSize: 10, className: 'elevation-axis-tick' }}
                axisLine={{ className: 'elevation-axis-line' }}
                tickLine={{ className: 'elevation-tick-line' }}
                tickFormatter={(value: number) => `${value}m`}
                width={42} // Increased from 40px to accommodate longer labels like "2500m"
              />
              <Area
                type="monotone"
                dataKey="elevation"
                fill="url(#areaColorGradient)"
                stroke="none"
                strokeWidth={0}
                isAnimationActive={false}
                legendType="none"
                baseValue={yAxisConfig.domain[0]}
              />
              {/* Line component with activeDot control */}
              <Line
                type="monotone"
                dataKey="elevation"
                className="elevation-line"
                dot={false}
                activeDot={
                  (hoverDistance !== null && hoverDistance !== undefined &&
                   hoverDistance >= zoomState.startDistance && 
                   hoverDistance <= zoomState.endDistance)
                    ? { 
                        r: 6,
                        fill: '#4a7c2a',
                        stroke: '#fff',
                        strokeWidth: 2,
                        className: 'elevation-active-dot'
                      }
                    : false
                }
                style={{ stroke: 'url(#colorGradient)' }}
                isAnimationActive={false}
              />
              
              {/* ReferenceLine with perfect alignment - use hoverDistance directly */}
              {hoverDistance !== null && hoverDistance !== undefined &&
               hoverDistance >= zoomState.startDistance && 
               hoverDistance <= zoomState.endDistance && (
                <ReferenceLine 
                  x={hoverDistance}
                  stroke="#4a7c2a" 
                  strokeWidth={2}
                  strokeDasharray="none"
                />
              )}
              
              {/* Climb highlighting with simple rectangular areas */}
              {climbHighlightRanges.length > 0 && climbHighlightRanges.map((range, index) => {
                // Use Y-axis domain directly for full height coverage
                const [yMin, yMax] = yAxisConfig.domain;
                
                // Use the actual visible data range (which is what the XAxis uses)
                const actualVisibleStart = visibleData.length > 0 ? visibleData[0].distance : 0;
                const actualVisibleEnd = visibleData.length > 0 ? visibleData[visibleData.length - 1].distance : totalDistance;
                
                // Check if climb overlaps with the visible range at all
                const isVisible = range.endDistance >= actualVisibleStart && range.startDistance <= actualVisibleEnd;
                
                if (!isVisible) return null;
                
                // Clip to visible range - Recharts doesn't render ReferenceArea properly if x1/x2 are outside axis domain
                const clippedX1 = Math.max(range.startDistance, actualVisibleStart);
                const clippedX2 = Math.min(range.endDistance, actualVisibleEnd);
                
                return (
                  <ReferenceArea
                    key={`climb-area-${index}`}
                    x1={clippedX1}
                    x2={clippedX2}
                    y1={yMin}
                    y2={yMax}
                    fill="#ff6b35"
                    fillOpacity={0.1}
                    stroke="none"
                    style={{ pointerEvents: 'none' }}
                    ifOverflow="visible"
                  />
                );
              })}
              
              {/* Climb highlighting vertical border lines */}
              {climbHighlightRanges.map((range, index) => {
                // Only render lines if climb overlaps with visible X-axis range
                const visibleStart = zoomState.isZoomed ? zoomState.startDistance : 0;
                const visibleEnd = zoomState.isZoomed ? zoomState.endDistance : totalDistance;
                
                const isVisible = range.endDistance >= visibleStart && range.startDistance <= visibleEnd;
                if (!isVisible) return null;
                
                // Only show start line if it's within visible range
                const showStartLine = range.startDistance >= visibleStart && range.startDistance <= visibleEnd;
                // Only show end line if it's within visible range
                const showEndLine = range.endDistance >= visibleStart && range.endDistance <= visibleEnd;
                
                return [
                  // Vertical start line
                  showStartLine && (
                    <ReferenceLine 
                      key={`climb-start-${index}`}
                      x={range.startDistance}
                      stroke="#ff6b35" 
                      strokeWidth={3}
                      strokeOpacity={0.8}
                      style={{ pointerEvents: 'none' }}
                    />
                  ),
                  // Vertical end line
                  showEndLine && (
                    <ReferenceLine 
                      key={`climb-end-${index}`}
                      x={range.endDistance}
                      stroke="#ff6b35" 
                      strokeWidth={3}
                      strokeOpacity={0.8}
                      style={{ pointerEvents: 'none' }}
                    />
                  )
                ].filter(Boolean);
              }).flat()}
              
              {/* Standard Tooltip - just to ensure proper Recharts behavior */}
              <Tooltip 
                content={() => null}
                cursor={false}
                allowEscapeViewBox={{ x: false, y: false }}
              />
              <defs>
                <linearGradient id="areaColorGradient" x1="0" y1="0" x2="1" y2="0">
                  {gradientSegments.map((segment, index) => {
                    const steepness = segment.avgSteepness; // Use raw steepness (positive/negative)
                    let color = '#4a7c2a'; // Green for downhill or < 4% uphill
                    
                    // Only color positive gradients (uphill)
                    if (steepness >= 12) {
                      color = '#8B0000'; // Dark red for > 12% uphill
                    } else if (steepness >= 8) {
                      color = '#ff0000'; // Red for 8-12% uphill
                    } else if (steepness >= 4) {
                      color = '#ff9800'; // Orange for 4-8% uphill
                    }
                    // Negative gradients (downhill) and < 4% keep default green color
                    
                    return (
                      <stop
                        key={`area-${index}`}
                        offset={`${segment.offset}%`}
                        stopColor={color}
                        stopOpacity={0.3}
                      />
                    );
                  })}
                </linearGradient>
                <linearGradient id="colorGradient">
                  {gradientSegments.map((segment, index) => {
                    const steepness = segment.avgSteepness; // Use raw steepness (positive/negative)
                    let color = '#4a7c2a'; // Green for downhill or < 4% uphill
                    
                    // Only color positive gradients (uphill)
                    if (steepness >= 12) {
                      color = '#8B0000'; // Dark red for > 12% uphill
                    } else if (steepness >= 8) {
                      color = '#ff0000'; // Red for 8-12% uphill
                    } else if (steepness >= 4) {
                      color = '#ff9800'; // Orange for 4-8% uphill
                    }
                    // Negative gradients (downhill) and < 4% keep default green color
                    
                    return (
                      <stop
                        key={index}
                        offset={`${segment.offset}%`}
                        stopColor={color}
                      />
                    );
                  })}
                </linearGradient>
              </defs>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* Mini overview bar positioned absolutely within chart container */}
        {totalDistance > 0 && (
          <div className="elevation-overview-bar" style={{
            position: 'absolute',
            bottom: '0px', // Aligned with bottom edge of chart container
            left: '40px', // Start after Y-axis (width: 40px)
            right: '10px', // Just chart margin (label removed)
            zIndex: 1000,
            background: 'none',
            border: 'none',
            padding: '0'
          }}>
            <div className="overview-track">
              <div 
                ref={overviewElementRef}
                className="overview-viewport"
                style={{
                  left: `${(zoomState.startDistance / totalDistance) * 100}%`,
                  width: `${((zoomState.endDistance - zoomState.startDistance) / totalDistance) * 100}%`,
                  cursor: 'grab',
                  position: 'relative'
                }}
                onMouseDown={handleOverviewMouseDown}
                onTouchStart={handleOverviewTouchStart}
              >
                {/* Left edge handle */}
                <div
                  className="overview-edge-handle overview-left-handle"
                  onMouseDown={handleLeftEdgeMouseDown}
                  onTouchStart={handleLeftEdgeTouchStart}
                />
                
                {/* Right edge handle */}
                <div
                  className="overview-edge-handle overview-right-handle"
                  onMouseDown={handleRightEdgeMouseDown}
                  onTouchStart={handleRightEdgeTouchStart}
                />
              </div>
              
              {/* Hover point marker on overview bar */}
              {activePoint !== null && activePoint !== undefined && data[activePoint] && (
                <div 
                  className="overview-hover-marker"
                  style={{
                    position: 'absolute',
                    left: `${(data[activePoint].distance / totalDistance) * 100}%`,
                    top: '-2px',
                    width: '2px',
                    height: '8px', // Adjust to match thinner track
                    backgroundColor: '#ff6b35', // Orange color to stand out
                    borderRadius: '1px',
                    pointerEvents: 'none',
                    zIndex: 1001, // Above viewport
                    transform: 'translateX(-50%)', // Center the marker
                    boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                  }}
                />
              )}
            </div>
            
            {/* Zoom level indicator with controls */}
            <div className="zoom-controls" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              color: '#666',
              marginTop: '0px',
              fontFamily: 'inherit',
              gap: '8px'
            }}>
              <IconButton 
                size="small" 
                onClick={handleZoomOut}
                onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                className="zoom-button zoom-out-button"
                sx={{ 
                  minWidth: '40px', 
                  width: '40px', 
                  height: '40px', 
                  padding: '4px',
                  color: '#666',
                  '&:hover': { color: '#333' }
                }}
              >
                <RemoveIcon sx={{ fontSize: '24px' }} />
              </IconButton>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: '120px', justifyContent: 'center' }}>
                <span>Zoom Level:</span>
                {zoomState.isZoomed && (
                  <IconButton 
                    size="small" 
                    onClick={resetZoom}
                    onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onTouchStart={(e) => { e.stopPropagation(); }}
                    className="zoom-button zoom-reset-button"
                    sx={{ 
                      minWidth: '24px', 
                      width: '24px', 
                      height: '24px', 
                      padding: '4px',
                      color: '#666',
                      '&:hover': { color: '#333' }
                    }}
                    title="Reset Zoom"
                  >
                    <ResetIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                )}
                <span>{Math.round((1 - ((zoomState.endDistance - zoomState.startDistance) / totalDistance)) * 100)}%</span>
              </div>
              
              <IconButton 
                size="small" 
                onClick={handleZoomIn}
                onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                className="zoom-button zoom-in-button"
                sx={{ 
                  minWidth: '40px', 
                  width: '40px', 
                  height: '40px', 
                  padding: '4px',
                  color: '#666',
                  '&:hover': { color: '#333' }
                }}
              >
                <AddIcon sx={{ fontSize: '24px' }} />
              </IconButton>
            </div>
          </div>
        )}
      </div>
      )}
    </Paper>
  );
};
